import { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EVENTS, Joyride, STATUS } from 'react-joyride';
import type { EventHandler, Step } from 'react-joyride';
import { TourTooltip } from './TourTooltip';
import { filterValidSteps, useJoyrideOptions } from './tourUtils';

interface PodcastDiscoveryTutorialProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

/** Delay (ms) after `open` flips true before resolving sidebar anchors. */
const ANCHOR_MOUNT_DELAY = 300;

export const PodcastDiscoveryTutorial = ({ open, onComplete, onSkip }: PodcastDiscoveryTutorialProps) => {
  const { t } = useTranslation('common');
  const joyrideOptions = useJoyrideOptions();
  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    if (!open) {
      setSteps([]);
      return;
    }

    const timer = window.setTimeout(() => {
      const candidates: Step[] = [
        {
          target: 'body',
          placement: 'center',
          title: `🎉 ${t('podcastDiscovery.congrats', 'Great! Project Created!')}`,
          content: (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('podcastDiscovery.subtitle', "Now let's find some podcasts to add to it")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                💡 {t('podcastDiscovery.tip', 'Tip: You can find these options in the sidebar menu anytime!')}
              </Typography>
            </>
          ),
        },
        {
          target: '[data-tour="nav-search-podcasts"]',
          placement: 'right',
          // Allow clicking the spotlit nav item (replaces the old CTA button)
          blockTargetInteraction: false,
          title: t('podcastDiscovery.searchTitle', 'Search All Podcasts'),
          content: t(
            'podcastDiscovery.searchDescription',
            'Find any podcast by name or keyword. Perfect when you know what you want to listen to.'
          ),
        },
        {
          target: '[data-tour="nav-popular-podcasts"]',
          placement: 'right',
          blockTargetInteraction: false,
          title: t('podcastDiscovery.popularTitle', 'Browse Popular Podcasts'),
          content: t(
            'podcastDiscovery.popularDescription',
            'Discover podcasts other Scribelate users are learning with. Filter by language to find the best content.'
          ),
        },
      ];

      // On mobile the temporary drawer is closed, so only the centered
      // intro step survives — acceptable.
      setSteps(filterValidSteps(candidates));
    }, ANCHOR_MOUNT_DELAY);

    return () => window.clearTimeout(timer);
  }, [open, t]);

  // Clicking a spotlit nav item navigates away and unmounts this tour, so
  // treat it as completion (same persistence as the old CTA buttons that
  // called onComplete() before navigating). Capture phase ensures this runs
  // before the navigation handler.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleNavClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          '[data-tour="nav-search-podcasts"], [data-tour="nav-popular-podcasts"]'
        )
      ) {
        onComplete();
      }
    };
    document.addEventListener('click', handleNavClick, true);
    return () => document.removeEventListener('click', handleNavClick, true);
  }, [open, onComplete]);

  const handleEvent: EventHandler = (data) => {
    if (data.type !== EVENTS.TOUR_END) {
      return;
    }
    if (data.status === STATUS.FINISHED) {
      // Persists podcast_discovery_completed + sessionStorage dismiss
      onComplete();
    } else if (data.status === STATUS.SKIPPED) {
      // sessionStorage dismiss only
      onSkip();
    }
  };

  if (!open || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run
      continuous
      steps={steps}
      tooltipComponent={TourTooltip}
      onEvent={handleEvent}
      options={{
        ...joyrideOptions,
        closeButtonAction: 'skip',
        overlayClickAction: false,
      }}
    />
  );
};
